/**
 * LayoutCard — entity card for a single catalog entity.
 *
 * Renders the same visual style as WorkflowCard from the scaffolder-workflows
 * plugin. Action button behaviour differs by contentType:
 *   - workflows → Launch button → /create/templates/{namespace}/{name}
 *   - docs      → View Docs button → /docs/{namespace}/{kind}/{name}
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Star as StarIcon, StarBorder as StarBorderIcon, PlayCircleFilled as PlayCircleFilledIcon } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CategoryIcon from '@mui/icons-material/Category';
import { renderCategoryIcon } from '../../iconRegistry';
import { useApi } from '@backstage/core-plugin-api';
import { techdocsApiRef } from '@backstage/plugin-techdocs-react';
import type { Entity } from '@backstage/catalog-model';
import type { ContentType, LayoutCategory } from '../../types';

// ---------------------------------------------------------------------------
// Type-chip colour map
// ---------------------------------------------------------------------------

const TYPE_CHIP_STYLES: Record<string, { bg: string; color: string }> = {
  automation:     { bg: '#EDE7F6', color: '#5C35CC' },
  service:        { bg: '#E8F5E9', color: '#2E7D32' },
  website:        { bg: '#E3F2FD', color: '#1565C0' },
  infrastructure: { bg: '#FFF3E0', color: '#E65100' },
  environment:    { bg: '#FFF3E0', color: '#E65100' },
  documentation:  { bg: '#E3F2FD', color: '#1565C0' },
};

function getTypeChipStyle(type: string) {
  return TYPE_CHIP_STYLES[type.toLowerCase()] ?? { bg: '#F3F4F6', color: '#6B7280' };
}

function getSpecField(entity: Entity, field: string): string {
  if (!entity.spec) return '';
  const val = (entity.spec as Record<string, unknown>)[field];
  return typeof val === 'string' ? val : '';
}

function formatOwner(owner: string): string {
  return owner.replace(/^(group|user):[^/]+\//, '');
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles()((theme) => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    transition: 'box-shadow 0.18s, transform 0.15s',
    '&:hover': { boxShadow: theme.shadows[4], transform: 'translateY(-2px)' },
  },
  cardTopRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.25, 1.25, 0.25, 1.5),
    gap: theme.spacing(0.25),
  },
  typeChip: {
    height: 20,
    fontSize: 10,
    fontWeight: 700,
    borderRadius: theme.shape.borderRadius,
    letterSpacing: '0.02em',
  },
  cardIconBtn: { padding: 4 },
  cardBody: {
    padding: theme.spacing(0.5, 1.5, 0.75),
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  cardTitleRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
    alignItems: 'flex-start',
  },
  cardCircle: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
  },
  cardTitle: { fontWeight: 700, fontSize: 13, lineHeight: 1.4 },
  cardOwner: { fontSize: 11, color: theme.palette.text.secondary },
  cardDesc: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    lineHeight: 1.55,
    flex: 1,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    marginBottom: theme.spacing(1.25),
  },
  tagsLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: theme.palette.text.secondary,
    marginBottom: 3,
  },
  tagsWrap: { display: 'flex', flexWrap: 'wrap' as const, gap: 3, marginBottom: theme.spacing(1) },
  tagChip: { height: 18, fontSize: 10, fontWeight: 600, borderRadius: theme.shape.borderRadius },
  cardLastUpdated: {
    fontSize: 11,
    color: theme.palette.text.disabled,
    marginTop: theme.spacing(0.75),
  },
  cardActionsWrap: { padding: theme.spacing(0.5, 1.5, 1.5) },
  actionBtn: {
    width: '100%',
    textTransform: 'none' as const,
    fontWeight: 700,
    fontSize: 13,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.75, 0),
  },
}));

function formatDate(timestamp: number): string {
  const d = new Date(timestamp);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface LayoutCardProps {
  entity: Entity;
  contentType: ContentType;
  category: LayoutCategory | undefined;
  isFavourite: boolean;
  onToggleFavourite: (name: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LayoutCard({
  entity,
  contentType,
  category,
  isFavourite,
  onToggleFavourite,
}: LayoutCardProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // undefined = still loading, null = loaded but no timestamp, number = timestamp
  const [buildTimestamp, setBuildTimestamp] = useState<number | null | undefined>(undefined);
  const techdocsApi = useApi(techdocsApiRef);

  useEffect(() => {
    if (contentType !== 'docs') return undefined;
    let cancelled = false;
    const ns = entity.metadata.namespace ?? 'default';
    techdocsApi
      .getTechDocsMetadata({ kind: entity.kind, namespace: ns, name: entity.metadata.name })
      .then((meta: any) => {
        if (!cancelled) {
          setBuildTimestamp(typeof meta?.build_timestamp === 'number' ? meta.build_timestamp : null);
        }
      })
      .catch(() => { if (!cancelled) setBuildTimestamp(null); });
    return () => { cancelled = true; };
  }, [techdocsApi, contentType, entity.kind, entity.metadata.namespace, entity.metadata.name]);

  const specType =
    getSpecField(entity, 'type') ||
    (contentType === 'docs' ? 'documentation' : 'automation');
  const owner = formatOwner(getSpecField(entity, 'owner'));
  const title = entity.metadata.title ?? entity.metadata.name;
  const description = entity.metadata.description ?? '';
  const tags = entity.metadata.tags ?? [];
  const chipStyle = getTypeChipStyle(specType);
  const accentColor = category?.accentColor ?? '#E6007E';
  const ns = entity.metadata.namespace ?? 'default';

  function handlePrimary() {
    if (contentType === 'workflows') {
      navigate(`/create/templates/${ns}/${entity.metadata.name}`);
    } else {
      navigate(`/docs/${ns}/${entity.kind.toLowerCase()}/${entity.metadata.name}`);
    }
  }

  function handleViewDetails() {
    navigate(
      `/catalog/${ns}/${entity.kind.toLowerCase()}/${entity.metadata.name}`,
    );
  }

  return (
    <Card className={classes.card} elevation={0}>
      {/* Type chip + star + kebab */}
      <div className={classes.cardTopRow}>
        <Chip
          label={specType}
          size="small"
          className={classes.typeChip}
          style={{ backgroundColor: chipStyle.bg, color: chipStyle.color }}
        />
        <div style={{ flex: 1 }} />
        <Tooltip
          title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
          arrow
        >
          <IconButton
            size="small"
            className={classes.cardIconBtn}
            onClick={e => {
              e.stopPropagation();
              const ref = `${entity.kind.toLowerCase()}:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;
              onToggleFavourite(ref);
            }}
          >
            {isFavourite ? (
              <StarIcon style={{ fontSize: 16, color: '#F59E0B' }} />
            ) : (
              <StarBorderIcon style={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
        <IconButton
          size="small"
          className={classes.cardIconBtn}
          onClick={e => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
        >
          <MoreVertIcon style={{ fontSize: 16 }} />
        </IconButton>
      </div>

      {/* Body */}
      <CardContent className={classes.cardBody}>
        <div className={classes.cardTitleRow}>
          <div
            className={classes.cardCircle}
            style={{ backgroundColor: `${accentColor}20` }}
          >
            {renderCategoryIcon(category?.icon, { style: { fontSize: 17, color: accentColor } }) ?? <CategoryIcon style={{ fontSize: 17, color: accentColor }} />}
          </div>
          <div>
            <Typography className={classes.cardTitle}>{title}</Typography>
            {owner && (
              <Typography className={classes.cardOwner}>By {owner}</Typography>
            )}
          </div>
        </div>

        {description && (
          <Typography className={classes.cardDesc}>{description}</Typography>
        )}

        {tags.length > 0 && (
          <>
            <Typography className={classes.tagsLabel}>Tags</Typography>
            <div className={classes.tagsWrap}>
              {tags.slice(0, 5).map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  className={classes.tagChip}
                />
              ))}
            </div>
          </>
        )}

        {contentType === 'docs' && buildTimestamp !== undefined && (
          <Typography className={classes.cardLastUpdated}>
            Last Updated: {buildTimestamp !== null ? formatDate(buildTimestamp) : 'N/A'}
          </Typography>
        )}
      </CardContent>

      {/* Action button */}
      <CardActions className={classes.cardActionsWrap}>
        <Button
          variant="contained"
          color="primary"
          className={classes.actionBtn}
          startIcon={
            contentType === 'workflows' ? (
              <PlayCircleFilledIcon style={{ fontSize: 17 }} />
            ) : (
              <MenuBookIcon style={{ fontSize: 17 }} />
            )
          }
          onClick={handlePrimary}
        >
          {contentType === 'workflows' ? 'Launch' : 'View Docs'}
        </Button>
      </CardActions>

      {/* Kebab menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          dense
          onClick={() => {
            setMenuAnchor(null);
            handlePrimary();
          }}
        >
          {contentType === 'workflows' ? 'Launch workflow' : 'View docs'}
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            setMenuAnchor(null);
            handleViewDetails();
          }}
        >
          View details in catalog
        </MenuItem>
      </Menu>
    </Card>
  );
}
